import { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { toast } from "sonner";
import {
  Mic,
  Globe,
  MapPin,
  CheckCircle2,
  Play,
  Star,
  ArrowLeft,
} from "lucide-react";

import narratorService from "@/services/narratorService";

export default function PublicNarratorProfile() {
  const { slug } = useParams();
  const navigate = useNavigate();

  const [loading, setLoading] = useState(true);
  const [profile, setProfile] = useState(null);
  const [playingDemo, setPlayingDemo] = useState(null);

  useEffect(() => {
    loadProfile();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [slug]);

  const loadProfile = async () => {
    try {
      setLoading(true);
      const data = await narratorService.getPublicProfile(slug);
      setProfile(data);
    } catch (error) {
      console.error("Error loading profile:", error);
      if (error.response?.status === 404) {
        toast.error("Narrator profile not found");
      }
    } finally {
      setLoading(false);
    }
  };

  const handlePlayDemo = (demo) => {
    if (playingDemo?.id === demo.id) {
      setPlayingDemo(null);
      toast.info("Audio paused");
    } else {
      setPlayingDemo(demo);
      toast.info(`Playing: ${demo.title} (Audio playback stub)`);
    }
  };

  /* ================= LOADING ================= */

  if (loading) {
    return (
      <div className="relative min-h-screen overflow-hidden flex items-center justify-center">

        {/* Background */}
        <img
          src="/images/dashboard-bg.png"
          alt="Background"
          className="fixed inset-0 w-full h-full object-cover -z-10"
        />

        {/* Overlay */}
        <div className="fixed inset-0 bg-black/10 -z-10"></div>

        <div className="text-center relative z-10">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto"></div>
          <p className="mt-4 text-slate-700">Loading profile...</p>
        </div>
      </div>
    );
  }

  /* ================= NOT FOUND ================= */

  if (!profile) {
    return (
      <div className="relative min-h-screen overflow-hidden flex items-center justify-center">

        {/* Background */}
        <img
          src="/images/dashboard-bg.png"
          alt="Background"
          className="fixed inset-0 w-full h-full object-cover -z-10"
        />

        {/* Overlay */}
        <div className="fixed inset-0 bg-black/10 -z-10"></div>

        <Card className="bg-white/90 backdrop-blur border-2 border-slate-200 max-w-md relative z-10">
          <CardContent className="py-12 text-center">
            <Mic className="w-16 h-16 mx-auto mb-4 text-slate-300" />
            <h2 className="text-xl font-semibold text-black mb-2">
              Narrator Not Found
            </h2>
            <p className="text-slate-600 mb-4">
              This narrator profile does not exist or has been removed.
            </p>
            <Button onClick={() => navigate("/")} variant="outline">
              <ArrowLeft className="w-4 h-4 mr-2" />
              Back to Home
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  const featuredDemo =
    profile.demos?.find((d) => d.is_featured) || profile.demos?.[0];

  /* ================= MAIN PAGE ================= */

  return (
    <div
      className="relative min-h-screen overflow-hidden"
      data-testid="public-narrator-profile"
    >

      {/* Background */}
      <img
        src="/images/dashboard-bg.png"
        alt="Background"
        className="fixed inset-0 w-full h-full object-cover -z-10"
      />

      {/* Overlay */}
      <div className="fixed inset-0 bg-black/10 -z-10"></div>

      {/* Header */}
      <header className="relative z-10 bg-white/80 backdrop-blur border-b border-slate-200">
        <div className="container mx-auto px-6 py-4 flex items-center justify-between">
          <h1
            className="text-2xl font-bold text-slate-900 cursor-pointer"
            style={{ fontFamily: "Playfair Display, serif" }}
            onClick={() => navigate("/")}
          >
            Golden Records
          </h1>

          <Button variant="outline" onClick={() => navigate("/")}>
            <ArrowLeft className="w-4 h-4 mr-2" />
            Back
          </Button>
        </div>
      </header>

      <main className="container mx-auto px-6 py-12 relative z-10">
        <div className="max-w-4xl mx-auto">

          {/* Profile Header */}
          <div className="text-center mb-12">
            <div className="w-32 h-32 rounded-full bg-gradient-to-r from-pink-500 via-purple-500 to-blue-500 flex items-center justify-center mx-auto mb-6 shadow-xl">
              <Mic className="w-16 h-16 text-white" />
            </div>

            <h1
              className="text-4xl font-bold text-black mb-2"
              style={{ fontFamily: "Playfair Display, serif" }}
            >
              {profile.display_name}
            </h1>

            <div className="flex flex-wrap justify-center gap-2 mb-4">
              {profile.is_verified && (
                <Badge className="bg-green-100 text-green-800 flex items-center gap-1">
                  <CheckCircle2 className="w-3 h-3" />
                  Verified
                </Badge>
              )}

              {profile.country && (
                <Badge variant="outline" className="flex items-center gap-1">
                  <MapPin className="w-3 h-3" />
                  {profile.country}
                </Badge>
              )}

              {profile.gender && (
                <Badge variant="outline">{profile.gender}</Badge>
              )}
            </div>

            {profile.bio && (
              <p className="text-slate-700 max-w-2xl mx-auto">
                {profile.bio}
              </p>
            )}
          </div>

          {/* Languages */}
          {profile.languages?.length > 0 && (
            <Card className="bg-white/90 backdrop-blur border-2 border-slate-200 mb-8">
              <CardContent className="py-6">
                <div className="flex items-center gap-2 mb-3">
                  <Globe className="w-5 h-5 text-purple-600" />
                  <h3 className="font-semibold text-black">Languages</h3>
                </div>

                <div className="flex flex-wrap gap-2">
                  {profile.languages.map((lang) => (
                    <Badge
                      key={lang}
                      className="bg-gradient-to-r from-pink-100 to-purple-100 text-purple-800"
                    >
                      {lang}
                    </Badge>
                  ))}
                </div>

                {profile.accent_tags && (
                  <p className="text-sm text-slate-600 mt-2">
                    Accents: {profile.accent_tags}
                  </p>
                )}
              </CardContent>
            </Card>
          )}

          {/* Featured Demo */}
          {featuredDemo && (
            <Card className="bg-gradient-to-r from-pink-50 via-purple-50 to-blue-50 border-2 border-purple-200 mb-8">
              <CardContent className="py-6">
                <div className="flex items-center gap-2 mb-4">
                  <Star className="w-5 h-5 text-yellow-500 fill-yellow-500" />
                  <h3 className="font-semibold text-black">
                    Featured Demo
                  </h3>
                </div>

                <div className="flex items-center justify-between bg-white rounded-lg p-4 border border-purple-200">
                  <div>
                    <h4 className="font-semibold text-black">
                      {featuredDemo.title}
                    </h4>
                  </div>

                  <Button
                    onClick={() => handlePlayDemo(featuredDemo)}
                    className={
                      playingDemo?.id === featuredDemo.id
                        ? "bg-purple-600"
                        : "bg-gradient-to-r from-pink-500 via-purple-500 to-blue-500"
                    }
                  >
                    <Play className="w-4 h-4 mr-1" />
                    {playingDemo?.id === featuredDemo.id
                      ? "Playing..."
                      : "Play"}
                  </Button>
                </div>
              </CardContent>
            </Card>
          )}

          {/* All Demos */}
          {profile.demos?.length > 0 && (
            <Card className="bg-white/90 backdrop-blur border-2 border-slate-200">
              <CardContent className="py-6">
                <h3 className="font-semibold text-black mb-4">
                  Demo Portfolio
                </h3>

                <div className="space-y-3">
                  {profile.demos.map((demo) => (
                    <div
                      key={demo.id}
                      className="flex items-center justify-between p-4 bg-slate-50 rounded-lg hover:bg-slate-100 transition-all"
                    >
                      <div>
                        <h4 className="font-medium text-black">
                          {demo.title}
                        </h4>
                      </div>

                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => handlePlayDemo(demo)}
                      >
                        <Play className="w-4 h-4" />
                      </Button>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          )}

          {/* No Demos */}
          {(!profile.demos || profile.demos.length === 0) && (
            <Card className="bg-white/90 backdrop-blur border-2 border-slate-200">
              <CardContent className="py-12 text-center">
                <Mic className="w-12 h-12 mx-auto mb-4 text-slate-300" />
                <p className="text-slate-600">
                  No public demos available yet.
                </p>
              </CardContent>
            </Card>
          )}
        </div>
      </main>
    </div>
  );
}
